/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { AuthProvider, createUserWithEmailAndPassword, EmailAuthProvider, FacebookAuthProvider, GoogleAuthProvider,
         linkWithCredential, linkWithPopup, reauthenticateWithCredential, reload, sendEmailVerification, sendPasswordResetEmail,
         signInWithEmailAndPassword, signInWithPopup, updatePassword, updateProfile, User } from "firebase/auth";
import { auth, firestoreService } from "./FirestoreService";
import { FirebaseError } from "firebase/app";

class AuthService {

    static handleError = (message: string, error: unknown, setErrors: (message: string) => void) => {
        if (error instanceof FirebaseError) {
          console.error(message + ", Firebase Error:", error.message);
          if (error.message === 'Firebase: Error (auth/invalid-credential).') {
            setErrors('Invalid credentials');
          } else {
            setErrors(error.message);
          }
        } else {
          console.error(message + ", Unknown Error:", error);
          setErrors(message);
        }
    };
      
    // Google Login
    static loginWithGoogle = async (setErrors: (message: string) => void, onComplete: () => void) => {
        await this.loginWith(new GoogleAuthProvider(), setErrors, onComplete);
    }
    // Facebook Login
    static loginWithFacebook = async (setErrors: (message: string) => void, onComplete: () => void) => {
        await this.loginWith(new FacebookAuthProvider(), setErrors, onComplete);
    }
    static loginWith = async (provider: AuthProvider, setErrors: (message: string) => void, onComplete: () => void) => {
        try {
            const result = await signInWithPopup(auth, provider);
            console.log("User signed in:", result.user);
            setErrors('');
            onComplete();
          } catch (error) {
            AuthService.handleError("Facebook login error", error, setErrors);
        }
    }

    static linkWithGoogle = async (onComplete: () => void) => {
        this.linkWith(new GoogleAuthProvider(), "Google", onComplete);
    }
    static linkWithFacebook = async (onComplete: () => void) => {
        this.linkWith(new FacebookAuthProvider(), "Facebook", onComplete);
    }
    static linkWith = async (provider: AuthProvider, name: string, onComplete: () => void) => {
        if (auth.currentUser) {
          try {
            const result = await linkWithPopup(auth.currentUser, provider);
            console.log(`Linked ${name} account: ${result.user}`);
            onComplete();
          } catch (error) {
            console.error(`Error linking ${name} account:`, error);
          }
        } else {
          console.log("No user is signed in to link accounts.");
        }
    };

    // Email/Password Login
    static loginWithEmail = async(email: string, password: string, setErrors: (message: string) => void): Promise<boolean> => {
        try {
            let result;
            if (auth.currentUser) {
                const credential = EmailAuthProvider.credential(email, password);
                result = await linkWithCredential(auth.currentUser, credential);

                // Send the email verification
                await sendEmailVerification(auth.currentUser);
                console.log("Verification email sent.");
                alert("A verification email has been sent to your email address. Please verify your email to complete the registration.");
            } else {
                result = await signInWithEmailAndPassword(auth, email.trim(), password);
                return result != null;
            }
            console.log("User signed in:", result.user);
            setErrors('');
            return true;
          } catch (error) {
            AuthService.handleError("Email login error", error, setErrors);
            return false;
        }
    }
   
    // static handleRegister = async (email: string, password: string, confirmPassword: string, userId: string, birthday: Date,
    //                                setErrors: (message: string) => void) => {
    //     if (password !== confirmPassword) {
    //       setErrors("Passwords do not match.");
    //       return;
    //     }
  
    //     if (!AuthService.isValidEmail(email)) {
    //       setErrors("Please enter a valid email address.");
    //       return;
    //     }
  
    //     try {
    //       await this.registerWithEmail(email.trim(), password, userId, birthday, setErrors);
    //       setErrors("");
    //     } catch (err) {
    //       AuthService.handleError("Create account error", err, setErrors);
    //     }
    //   };

    //   static errorMessages: Record<string, string> = {
    //     "auth/email-already-in-use":  "Email is already in use.",
    //     "auth/weak-password":         "Password must be at least 6 characters.",
    //     "auth/invalid-email":         "The email address is invalid.",
    //     "auth/operation-not-allowed": "Email/password accounts are not enabled.",
    //     "auth/user-not-found":        "No account found with this email address.",
    //     "auth/wrong-password":        "Incorrect password.",
    // };
    
    static registerWithEmail = async (email: string, password: string, userId: string, birthday: Date,
                                      setErrors: (message: string) => void): Promise<User | null> => {
        if (!AuthService.isValidEmail(email)) {
            setErrors("Please enter a valid email address.");
            return null;
        }

        // Validate birthday
        const today = new Date();
        const minBirthDate = new Date();
        minBirthDate.setFullYear(today.getFullYear() - 13); // 13 years ago today.
        if (birthday > minBirthDate) {
          setErrors("You must be at least 13 years old to create an account.");
          return null;
        }

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
            console.log("User registered successfully:", userCredential.user);

            // Update Firebase Auth profile
            await updateProfile(userCredential.user, { displayName: userId });

            // Register user, Store birthday & Acceptance of terms in Firestore
            const reg = await firestoreService.registerUsername(userCredential.user.uid, userId, birthday);
            if (!reg.success) {
              // Surface the specific reason (e.g. reserved / offensive name, taken, under 13).
              setErrors(reg.error || 'Account created, but the username could not be registered.');
              return null;
            }
            
            // Send the email verification
            await sendEmailVerification(userCredential.user);
            console.log("Verification email sent.");
            alert("A verification email has been sent to your email address. Please verify your email to complete the registration.");

            setErrors('');
            return userCredential.user;
        } catch (err: unknown) {
            AuthService.handleError("Registration error", err, setErrors);
            return null;
        }
    }

    static resendConfirmationEmail = async (user: User, setErrors: (message: string) => void) => {
        try {
            // Send the email verification
            if (user) {
                await sendEmailVerification(user);
                console.log("Verification email resent.");
                alert("Another verification email has been sent to your email address. Please verify your email to complete the registration.");
                setErrors('');
            } else {
                setErrors('User not logged in.');
            }
        } catch (err: unknown) {
            AuthService.handleError("Confirmation email error", err, setErrors);
        }
    }

    static refreshUser = async (user: User, setErrors: (message: string) => void): Promise<(User | null)> => {
        try {
          if (user) {
            await reload(user);
            if (user.emailVerified) {
              console.log("User email verified!");
            }
          }
          return user;
        } catch (error) {
            AuthService.handleError("Refresh User error", error, setErrors);
            return null;
        }
      };

      static changeUserPassword = async (user: User, currentPassword: string, newPassword: string, setErrors: (message: string) => void): Promise<boolean> => {
        if (!user) {
          console.error("No user is currently logged in.");
          return false;
        }
      
        try {
          // Step 1: Reauthenticate the user
          const credential = EmailAuthProvider.credential(user.email as string, currentPassword);
          await reauthenticateWithCredential(user, credential);
          console.log("Reauthentication successful.");
      
          // Step 2: Update the password
          await updatePassword(user, newPassword);
          console.log("Password updated successfully.");
          setErrors('');
          return true;
        } catch (error) {
          AuthService.handleError("Changing password error", error, setErrors);
          return false;
        }
      }


    static handlePasswordReset = async (email: string, setErrors: (message: string) => void) => {
        try {
          await sendPasswordResetEmail(auth, email.trim());
          alert("Password reset email sent! Check your inbox.");
        } catch (error: any) {
          setErrors(`Error: ${error.message}`);
        }
    };

    static isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    // Username can only contain letters, numbers, underscores, and hyphens (3-30 characters).
    static isValidUserId = (userId: string): boolean => /^[a-zA-Z0-9_-]{3,30}$/.test(userId);
    static isValidPassword = (password: string): boolean => /^.{6,}$/.test(password);

}

export default AuthService